import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PAYOUT_RULES } from '@lk/shared';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Panel } from '../../components/Panel';
import { hryvniaLabel } from '../../lib/format';
import { humanMessage } from '../../lib/errors';
import { withdrawCoins, type WalletResponse } from '../../lib/api';
import { walletQuery } from '../learner/queries';
import { playSound } from '../../audio/sound';

/**
 * Вивід зароблених балів.
 *
 * Поріг у 1000 грн навмисний: дрібні щоденні виплати перетворюють навчання
 * на касу, а накопичення до відчутної суми — це та сама фінансова
 * грамотність, яку ми тренуємо в задачах.
 *
 * Суму «всі доступні» на сервер НЕ передаємо числом: поки дитина обирає,
 * черга спроб може догнати ще кілька балів, і застаріле число з екрана
 * розійшлося б із реальним балансом. Сервер рахує сам.
 */
export function WithdrawPanel({
  learnerId,
  wallet,
}: {
  learnerId: string;
  wallet: WalletResponse | undefined;
}): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'all' | 'exact'>('all');
  const [amount, setAmount] = useState<number>(PAYOUT_RULES.MIN_WITHDRAWAL);
  const [done, setDone] = useState<number | null>(null);

  const balance = wallet?.balance ?? 0;
  const enough = balance >= PAYOUT_RULES.MIN_WITHDRAWAL;
  const toGo = PAYOUT_RULES.MIN_WITHDRAWAL - balance;

  const withdraw = useMutation({
    mutationFn: () =>
      withdrawCoins({
        learnerId,
        request: mode === 'all' ? { mode: 'all' } : { mode: 'exact', amount },
      }),
    onSuccess: (next) => {
      const taken = balance - next.balance;
      queryClient.setQueryData(walletQuery(learnerId).queryKey, next);
      void queryClient.invalidateQueries({ queryKey: walletQuery(learnerId).queryKey });
      playSound('coin');
      setDone(taken > 0 ? taken : null);
      setOpen(false);
    },
  });

  if (!wallet) return null;

  if (done !== null) {
    return (
      <Panel tight className="border-[var(--lk-green)]/50 p-4 text-center">
        <Icon name="correct" size={40} className="mx-auto" />
        <p className="mt-2 text-base font-black">Заявку прийнято</p>
        <p className="mt-1 text-sm font-bold text-white/70">
          {hryvniaLabel(done)} знято з балансу. Батьки вже отримали повідомлення.
        </p>
        <Button tone="ghost" mute block className="mt-3" onClick={() => setDone(null)}>
          Гаразд
        </Button>
      </Panel>
    );
  }

  if (!open) {
    return (
      <Button
        tone={enough ? 'gold' : 'ghost'}
        block
        mute
        disabled={!enough}
        onClick={() => {
          setOpen(true);
          setMode('all');
          setAmount(Math.max(PAYOUT_RULES.MIN_WITHDRAWAL, balance));
        }}
        data-testid="withdraw-open"
      >
        <Icon name="coin" size={20} />
        {enough
          ? 'Вивести бали'
          : `Вивести можна від ${PAYOUT_RULES.MIN_WITHDRAWAL} грн — ще ${hryvniaLabel(toGo)}`}
      </Button>
    );
  }

  const invalid =
    mode === 'exact' && (amount < PAYOUT_RULES.MIN_WITHDRAWAL || amount > balance);

  return (
    <Panel tight className="p-4">
      <p className="text-sm font-black">Скільки вивести?</p>
      <p className="mt-1 text-xs font-bold text-white/55">
        Доступно {hryvniaLabel(balance)} · мінімум {PAYOUT_RULES.MIN_WITHDRAWAL} грн
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button
          tone={mode === 'all' ? 'gold' : 'ghost'}
          mute
          onClick={() => setMode('all')}
          data-testid="withdraw-mode-all"
        >
          Усі доступні
        </Button>
        <Button
          tone={mode === 'exact' ? 'gold' : 'ghost'}
          mute
          onClick={() => setMode('exact')}
          data-testid="withdraw-mode-exact"
        >
          Певна сума
        </Button>
      </div>

      {mode === 'exact' ? (
        <label className="mt-3 block text-xs font-black tracking-wide text-white/60 uppercase">
          Сума, грн
          <input
            type="number"
            inputMode="numeric"
            min={PAYOUT_RULES.MIN_WITHDRAWAL}
            max={balance}
            step={1}
            value={amount}
            onChange={(e) => setAmount(Math.floor(Number(e.target.value) || 0))}
            className="lk-panel-tight mt-1 h-13 w-full px-4 text-lg font-black text-white outline-none"
            data-testid="withdraw-amount"
          />
        </label>
      ) : null}

      {invalid ? (
        <p className="mt-2 text-xs font-bold text-[var(--lk-red)]">
          {amount < PAYOUT_RULES.MIN_WITHDRAWAL
            ? `Мінімум ${PAYOUT_RULES.MIN_WITHDRAWAL} грн`
            : `На балансі лише ${hryvniaLabel(balance)}`}
        </p>
      ) : null}

      {withdraw.isError ? (
        <p className="mt-2 text-xs font-bold text-[var(--lk-red)]" role="alert">
          {humanMessage(withdraw.error)}
        </p>
      ) : null}

      <div className="mt-3 flex gap-2">
        <Button tone="ghost" mute block onClick={() => setOpen(false)}>
          Скасувати
        </Button>
        <Button
          tone="gold"
          block
          mute
          disabled={invalid}
          loading={withdraw.isPending}
          onClick={() => withdraw.mutate()}
          data-testid="withdraw-confirm"
        >
          Вивести {mode === 'all' ? hryvniaLabel(balance) : hryvniaLabel(amount)}
        </Button>
      </div>
    </Panel>
  );
}
